import { Fragment, useLayoutEffect, useMemo, useRef, type ReactNode, type RefObject } from 'react';
import type { PageContext, PageDefinition } from '../lib/types';
import { Markup } from './Markup';

let pageInstances = 0;
/** Existing page mount hooks own only the content below this React boundary. */
export interface ViewSnapshot { path: string; scroll: number; filter: string; caret: number | null }
// `notice` renders inside <main>: the app shell is a grid, so an extra sibling would take the main column.
export function PageView({ page, context, previous, notice }: { page: PageDefinition; context: PageContext; previous: RefObject<ViewSnapshot>; notice?: ReactNode }) {
  const ref = useRef<HTMLElement>(null);
  const drafts = useRef(new Map<string, string>());
  const draftScope = `${context.path}?${new URLSearchParams(context.query).toString()}`;
  const scope = useRef(draftScope);
  if (scope.current !== draftScope) { drafts.current.clear(); scope.current = draftScope; }
  const rememberDraft = (target: EventTarget | null) => {
    if (!(target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement)) return;
    const form = target.closest('[data-project-form], [data-workspace-form]');
    if (!form || !target.name || target instanceof HTMLInputElement && ['file', 'password'].includes(target.type)) return;
    drafts.current.set(`${form.hasAttribute('data-project-form') ? 'data-project-form' : 'data-workspace-form'}:${target.name}`, target.value);
  };
  // Mount hooks attach listeners without cleanup, so every page instance gets fresh DOM;
  // reconciling onto the previous markup would stack a listener per rerender.
  const instance = useMemo(() => ++pageInstances, [page]);
  useLayoutEffect(() => {
    const root = ref.current!;
    if (previous.current.path === context.path) {
      root.scrollTop = previous.current.scroll;
      const filter = previous.current.filter;
      if (filter) {
        const input = root.querySelector<HTMLInputElement>(`[data-filter="${CSS.escape(filter)}"]`);
        input?.focus();
        if (previous.current.caret != null && input && ['text', 'search', 'url', 'tel', 'password'].includes(input.type)) input.setSelectionRange(previous.current.caret, previous.current.caret);
      }
    } else root.scrollTop = 0;
    document.title = `BnB Nodes — холст нейросетей: цепочка моделей одной кнопкой · ${page.title}`;
    const cleanup = page.mount?.(root, context);
    // Background table refreshes replace template DOM. Keep unsaved settings
    // until the server reflects them, or the user leaves this settings route.
    for (const [key, value] of drafts.current) {
      const [form, name] = key.split(':');
      const control = root.querySelector<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(`[${form}] [name="${CSS.escape(name)}"]`);
      if (!control) continue;
      if (control.value === value) drafts.current.delete(key);
      else control.value = value;
    }
    return () => {
      const active = document.activeElement as HTMLInputElement | null;
      previous.current = { path: context.path, scroll: root.scrollTop, filter: active?.dataset.filter || '', caret: active?.selectionStart ?? null };
      if (typeof cleanup === 'function') cleanup();
    };
  }, [page, context, previous]);
  return <main ref={ref} className={`main ${page.mainClass || ''}`} onInput={event => rememberDraft(event.target)} onChange={event => rememberDraft(event.target)}>
    {notice}
    {page.content ?? <Fragment key={instance}><Markup value={page.body || ''} /></Fragment>}
  </main>;
}
